interface PerformanceMetrics {
  queryTime: number
  cacheHitRate: number
  memoryUsage: number
  timestamp: number
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetrics[]> = new Map()
  private readonly MAX_METRICS_PER_KEY = 100

  /**
   * Record performance metrics for a specific operation
   */
  recordMetrics(key: string, metrics: Omit<PerformanceMetrics, "timestamp">) {
    const fullMetrics: PerformanceMetrics = {
      ...metrics,
      timestamp: Date.now(),
    }

    if (!this.metrics.has(key)) {
      this.metrics.set(key, [])
    }

    const keyMetrics = this.metrics.get(key)!
    keyMetrics.push(fullMetrics)

    // Keep only the last MAX_METRICS_PER_KEY metrics
    if (keyMetrics.length > this.MAX_METRICS_PER_KEY) {
      keyMetrics.splice(0, keyMetrics.length - this.MAX_METRICS_PER_KEY)
    }
  }

  /**
   * Get performance statistics for a specific operation
   */
  getStats(key: string) {
    const keyMetrics = this.metrics.get(key)
    if (!keyMetrics || keyMetrics.length === 0) {
      return null
    }

    const queryTimes = keyMetrics.map(m => m.queryTime)
    const cacheHitRates = keyMetrics.map(m => m.cacheHitRate)
    const memoryUsages = keyMetrics.map(m => m.memoryUsage)

    return {
      count: keyMetrics.length,
      avgQueryTime: this.calculateAverage(queryTimes),
      minQueryTime: Math.min(...queryTimes),
      maxQueryTime: Math.max(...queryTimes),
      avgCacheHitRate: this.calculateAverage(cacheHitRates),
      avgMemoryUsage: this.calculateAverage(memoryUsages),
      lastUpdate: keyMetrics[keyMetrics.length - 1].timestamp,
    }
  }

  /**
   * Get all performance statistics
   */
  getAllStats() {
    const stats: Record<string, any> = {}
    for (const [key] of this.metrics) {
      stats[key] = this.getStats(key)
    }
    return stats
  }

  /**
   * Clear metrics for a specific key
   */
  clearMetrics(key: string) {
    this.metrics.delete(key)
  }

  /**
   * Clear all metrics
   */
  clearAllMetrics() {
    this.metrics.clear()
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics() {
    return Object.fromEntries(this.metrics)
  }

  private calculateAverage(numbers: number[]): number {
    if (numbers.length === 0) {
      return 0
    }
    return numbers.reduce((sum, num) => sum + num, 0) / numbers.length
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor()

// Convenience functions for common operations
export const recordBalanceFetchMetrics = (
  operation: "overtime" | "azuro",
  queryTime: number,
  cacheHitRate: number,
  memoryUsage: number
) => {
  performanceMonitor.recordMetrics(`balance_fetch_${operation}`, {
    queryTime,
    cacheHitRate,
    memoryUsage,
  })
}

export const recordCacheMetrics = (
  operation: string,
  cacheHit: boolean,
  queryTime: number
) => {
  performanceMonitor.recordMetrics(`cache_${operation}`, {
    queryTime,
    cacheHitRate: cacheHit ? 1 : 0,
    memoryUsage: 0, // Memory usage not relevant for cache operations
  })
}

export default performanceMonitor
